import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/config/query-keys';
import { PricesApi } from '@/lib/prices-api';
import { classifyShowcaseError } from '@/lib/price-errors';
import type { ApiError } from '@/types/api';
import type { PeriodPrice, ShowcasePricesParams } from '@/types/price';

export function useShowcasePrices(
  params: ShowcasePricesParams,
  options?: { enabled?: boolean; selectedPeriod?: string },
) {
  const hasPromo = Boolean(params.promo?.trim());
  const enabled = options?.enabled ?? Boolean(
    params.facility && params.productCode && params.startedAt,
  );

  const query = useQuery({
    queryKey: queryKeys.prices.showcase(params),
    queryFn: () => PricesApi.getShowcasePrices(params),
    enabled,
    staleTime: 30_000,
    retry: false,
  });

  const errorKind = query.error
    ? classifyShowcaseError(query.error as ApiError, hasPromo)
    : undefined;

  const selectedPrice = useMemo((): PeriodPrice | undefined => {
    if (!query.data?.items || !options?.selectedPeriod) {
      return undefined;
    }
    return query.data.items.find((item) => item.period === options.selectedPeriod);
  }, [query.data, options?.selectedPeriod]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error as ApiError | null,
    errorKind,
    selectedPrice,
    refetch: query.refetch,
  };
}
